import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api, IMG } from "../lib/api";
import { ArrowRight, Loader2, BookOpen } from "lucide-react";
import EmailCapture from "../components/EmailCapture";

const formatDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
  } catch {
    return "";
  }
};

const BlogList = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = "Blog — Bedford & Universal Studios Travel Guides | Visit Bedford";
    api
      .get("/articles")
      .then((res) => setArticles(res.data))
      .finally(() => setLoading(false));
  }, []);

  const featured = articles[0];
  const rest = articles.slice(1);

  return (
    <div data-testid="blog-page">
      {/* HERO */}
      <section className="relative h-[55vh] min-h-[420px] w-full overflow-hidden">
        <img src={IMG.riverHouses} alt="Bedford riverside" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 hero-overlay" />
        <div className="absolute inset-0 flex items-end">
          <div className="container-site pb-14 md:pb-20">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9 }}
              className="max-w-4xl"
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/15 backdrop-blur-md border border-white/25 text-white text-xs font-semibold tracking-[0.2em] uppercase mb-5">
                <BookOpen className="w-3.5 h-3.5" /> The Bedford Journal
              </div>
              <h1 className="font-serif-display text-4xl md:text-6xl lg:text-7xl font-black text-white leading-[0.95] text-balance mb-4">
                Stories, guides &amp; insider tips.
              </h1>
              <p className="text-white/90 text-lg md:text-xl max-w-2xl">
                Long-form travel writing about Bedford, Universal Studios and the Great Ouse — written by the people who live here.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Articles grid */}
      <section className="bg-[#FAFAFA] py-16 md:py-24">
        <div className="container-site">
          {loading ? (
            <div className="py-20 flex items-center justify-center text-slate-500">
              <Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading articles…
            </div>
          ) : articles.length === 0 ? (
            <div className="py-20 text-center text-slate-500">
              <p className="font-serif-display text-2xl text-[#0B2C5B] mb-2">No articles yet.</p>
              <p>Check back soon — we publish new guides every week.</p>
            </div>
          ) : (
            <>
              {/* Featured */}
              {featured && (
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.7 }}
                  className="mb-16 md:mb-24"
                >
                  <Link to={`/blog/${featured.slug}`} className="grid grid-cols-1 lg:grid-cols-12 gap-8 md:gap-12 items-center group" data-testid="blog-featured">
                    <div className="lg:col-span-7 relative rounded-3xl overflow-hidden aspect-[16/10] md:aspect-[16/11]">
                      <img src={featured.hero_image} alt={featured.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                      <div className="absolute top-5 left-5 inline-flex items-center gap-2 bg-white/95 backdrop-blur-sm rounded-full px-4 py-2 text-xs font-bold tracking-[0.2em] uppercase text-[#FF8C00]">
                        Featured
                      </div>
                    </div>
                    <div className="lg:col-span-5">
                      <div className="text-xs font-semibold tracking-[0.25em] uppercase text-[#2AB7E8] mb-3">
                        {formatDate(featured.published_at || featured.created_at)}
                        {featured.tags?.length > 0 && <span className="text-slate-300 mx-2">·</span>}
                        {featured.tags?.[0] && <span className="text-[#0B2C5B]/70 normal-case tracking-normal">{featured.tags[0]}</span>}
                      </div>
                      <h2 className="font-serif-display text-3xl md:text-5xl font-bold text-[#0B2C5B] leading-[1.05] mb-4 group-hover:text-[#FF8C00] transition-colors">
                        {featured.title}
                      </h2>
                      {featured.subtitle && (
                        <p className="text-lg text-[#0B2C5B]/70 italic mb-4 font-medium">{featured.subtitle}</p>
                      )}
                      <p className="text-base md:text-lg text-slate-700 leading-relaxed mb-6">{featured.excerpt}</p>
                      <span className="inline-flex items-center gap-2 text-[#0B2C5B] font-semibold border-b-2 border-[#FF8C00] pb-1 group-hover:gap-3 transition-all">
                        Read article <ArrowRight className="w-4 h-4" />
                      </span>
                    </div>
                  </Link>
                </motion.div>
              )}

              {/* Grid */}
              {rest.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10">
                  {rest.map((a, i) => (
                    <motion.article
                      key={a.id}
                      initial={{ opacity: 0, y: 30 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true, margin: "-60px" }}
                      transition={{ duration: 0.6, delay: i * 0.08 }}
                      data-testid={`blog-card-${a.slug}`}
                    >
                      <Link to={`/blog/${a.slug}`} className="block group">
                        <div className="rounded-2xl overflow-hidden aspect-[4/3] mb-5">
                          <img src={a.hero_image} alt={a.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                        </div>
                        <div className="text-xs font-semibold tracking-[0.25em] uppercase text-[#2AB7E8] mb-2">
                          {formatDate(a.published_at || a.created_at)}
                          {a.tags?.[0] && <><span className="text-slate-300 mx-2">·</span><span className="text-[#0B2C5B]/70 normal-case tracking-normal">{a.tags[0]}</span></>}
                        </div>
                        <h3 className="font-serif-display text-2xl md:text-[1.7rem] font-bold text-[#0B2C5B] leading-tight mb-3 group-hover:text-[#FF8C00] transition-colors">
                          {a.title}
                        </h3>
                        <p className="text-slate-700 leading-relaxed mb-3 line-clamp-3">{a.excerpt}</p>
                        <span className="inline-flex items-center gap-2 text-sm font-semibold text-[#0B2C5B] group-hover:gap-3 transition-all">
                          Read more <ArrowRight className="w-4 h-4" />
                        </span>
                      </Link>
                    </motion.article>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </section>

      <EmailCapture source="blog" />
    </div>
  );
};

export default BlogList;
